import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimePortfolio
 * Subscribes to portfolio_holdings and player_stats for a specific user.
 * Provides: syncing badge, real-time net worth, instant holding updates.
 */
export function useRealtimePortfolio(userId, onHoldingChange, onStatsChange, options = {}) {
  const { enabled = true, pollIntervalMs = 30000 } = options;

  const [status, setStatus] = useState('idle'); // idle | connecting | connected | syncing | error | closed
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastSyncLabel, setLastSyncLabel] = useState('');
  const [lastSyncTs, setLastSyncTs] = useState(null);

  const holdingsChannelRef = useRef(null);
  const statsChannelRef = useRef(null);
  const pollingRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const mountedRef = useRef(true);
  const syncTimerRef = useRef(null);
  const MAX_RETRIES = 5;

  const formatSyncLabel = useCallback((ts) => {
    if (!ts) return '';
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 5) return 'Synced just now';
    if (diff < 60) return `Synced ${diff}s ago`;
    return `Synced ${Math.floor(diff / 60)}m ago`;
  }, []);

  useEffect(() => {
    if (!lastSyncTs) return;
    const interval = setInterval(() => {
      if (mountedRef?.current) setLastSyncLabel(formatSyncLabel(lastSyncTs));
    }, 5000);
    return () => clearInterval(interval);
  }, [lastSyncTs, formatSyncLabel]);

  const markSynced = useCallback(() => {
    const now = Date.now();
    setLastSyncTs(now);
    setLastSyncLabel('Synced just now');
    setIsSyncing(false);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef?.current) {
      clearInterval(pollingRef?.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((fetchFn) => {
    stopPolling();
    if (fetchFn) {
      pollingRef.current = setInterval(() => {
        if (mountedRef?.current) {
          console.log('[PortfolioRealtime] Polling fallback triggered');
          fetchFn();
        }
      }, pollIntervalMs);
    }
  }, [pollIntervalMs, stopPolling]);

  const cleanup = useCallback(() => {
    if (retryTimerRef?.current) {
      clearTimeout(retryTimerRef?.current);
      retryTimerRef.current = null;
    }
    if (syncTimerRef?.current) {
      clearTimeout(syncTimerRef?.current);
      syncTimerRef.current = null;
    }
    if (holdingsChannelRef?.current) {
      supabase?.removeChannel(holdingsChannelRef?.current);
      holdingsChannelRef.current = null;
    }
    if (statsChannelRef?.current) {
      supabase?.removeChannel(statsChannelRef?.current);
      statsChannelRef.current = null;
    }
    stopPolling();
  }, [stopPolling]);

  const subscribe = useCallback((fallbackFetch) => {
    if (!mountedRef?.current || !enabled || !userId) return;
    cleanup();

    setStatus('connecting');
    setIsConnected(false);
    console.log('[PortfolioRealtime] Subscribing for user:', userId);

    let holdingsConnected = false;
    let statsConnected = false;

    const checkAllConnected = () => {
      if (holdingsConnected && statsConnected) {
        setStatus('connected');
        setIsConnected(true);
        retryCountRef.current = 0;
        stopPolling();
        console.log('[PortfolioRealtime] All channels connected');
      }
    };

    const handleError = (channelName, statusCode, err) => {
      if (!mountedRef?.current) return;
      console.warn(`[PortfolioRealtime] ${channelName} error:`, statusCode, err);
      setStatus('error');
      setIsConnected(false);
      startPolling(fallbackFetch);

      if (retryCountRef?.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const delay = 3000 * Math.pow(1.5, retryCountRef?.current - 1);
        retryTimerRef.current = setTimeout(() => {
          if (mountedRef?.current) subscribe(fallbackFetch);
        }, delay);
      } else {
        setStatus('closed');
      }
    };

    // Holdings channel
    const holdingsChannel = supabase?.channel(`portfolio-holdings-rt-${userId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'portfolio_holdings',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (!mountedRef?.current) return;
          console.log('[PortfolioRealtime] Holdings change:', payload?.eventType);
          setIsSyncing(true);
          if (syncTimerRef?.current) clearTimeout(syncTimerRef?.current);
          if (onHoldingChange) onHoldingChange(payload);
          syncTimerRef.current = setTimeout(() => {
            if (mountedRef?.current) markSynced();
          }, 500);
        }
      )?.subscribe((st, err) => {
        if (!mountedRef?.current) return;
        console.log('[PortfolioRealtime] Holdings channel status:', st);
        if (st === 'SUBSCRIBED') {
          holdingsConnected = true;
          checkAllConnected();
        } else if (st === 'CHANNEL_ERROR' || st === 'TIMED_OUT' || st === 'CLOSED') {
          handleError('holdings', st, err);
        }
      });

    // Player stats channel
    const statsChannel = supabase?.channel(`player-stats-rt-${userId}`)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'player_stats',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (!mountedRef?.current) return;
          console.log('[PortfolioRealtime] Stats change: cash/networth update');
          setIsSyncing(true);
          if (syncTimerRef?.current) clearTimeout(syncTimerRef?.current);
          if (onStatsChange) onStatsChange(payload);
          syncTimerRef.current = setTimeout(() => {
            if (mountedRef?.current) markSynced();
          }, 500);
        }
      )?.subscribe((st, err) => {
        if (!mountedRef?.current) return;
        console.log('[PortfolioRealtime] Stats channel status:', st);
        if (st === 'SUBSCRIBED') {
          statsConnected = true;
          checkAllConnected();
        } else if (st === 'CHANNEL_ERROR' || st === 'TIMED_OUT' || st === 'CLOSED') {
          handleError('stats', st, err);
        }
      });

    holdingsChannelRef.current = holdingsChannel;
    statsChannelRef.current = statsChannel;
  }, [enabled, userId, cleanup, onHoldingChange, onStatsChange, markSynced, startPolling, stopPolling]);

  const forceReconnect = useCallback((fallbackFetch) => {
    console.log('[PortfolioRealtime] Manual reconnect triggered');
    retryCountRef.current = 0;
    subscribe(fallbackFetch);
  }, [subscribe]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  return {
    status,
    isSyncing,
    isConnected,
    lastSyncLabel,
    subscribe,
    forceReconnect,
  };
}
